import Link from '@mui/material/Link';
import React from 'react';

import {
  makeStyles,
  Theme,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import ScheduleIcon from '@material-ui/icons/Schedule';

import { resourceGroupArmId } from '../fixes/ResourceGroupTagFix/recentTagFixes';
import { useRecentTagFixes } from '../fixes/ResourceGroupTagFix/useRecentTagFixes';

const useStyles = makeStyles((theme: Theme) => ({
  tableRow: { '&:hover': { backgroundColor: theme.palette.action.hover } },
  status: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
  },
  notSet: { color: theme.palette.error.main },
  waiting: { color: theme.palette.info.main },
}));

const formatTime = (time: number) =>
  new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** One entry of a Resource Group Tag check's `untagged_resource_groups`. */
export type UntaggedResourceGroup = {
  account_id?: string;
  account_name?: string;
  resource_group: string;
};

export const isResourceGroupList = (
  values: any[],
): values is UntaggedResourceGroup[] =>
  values.length > 0 &&
  typeof values[0] === 'object' &&
  values[0] !== null &&
  'resource_group' in values[0];

export const buildResourceGroupPortalUrl = (
  subscriptionId: string,
  resourceGroup: string,
) =>
  `https://portal.azure.com/#@/resource/subscriptions/${encodeURIComponent(
    subscriptionId,
  )}/resourceGroups/${encodeURIComponent(resourceGroup)}/overview`;

export const ResourceGroupBreakdown = ({
  resourceGroups,
  tagKey,
}: {
  resourceGroups: UntaggedResourceGroup[];
  /** The tag the check reads; adds a status column tracking fixes made here. */
  tagKey?: string | null;
}) => {
  const classes = useStyles();
  const recentFixes = useRecentTagFixes(tagKey);

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Resource Group</TableCell>
          <TableCell>Subscription</TableCell>
          {tagKey && <TableCell>Status</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {resourceGroups.map((rg, i) => {
          const subscriptionLabel = rg.account_name || rg.account_id || 'N/A';
          const fixedAt = rg.account_id
            ? recentFixes.get(
                resourceGroupArmId(rg.account_id, rg.resource_group),
              )
            : undefined;
          return (
            <TableRow key={i} className={classes.tableRow}>
              <TableCell>
                {rg.account_id ? (
                  <Link
                    href={buildResourceGroupPortalUrl(
                      rg.account_id,
                      rg.resource_group,
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: '0.8rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 3,
                    }}
                  >
                    {rg.resource_group}
                    <OpenInNewIcon style={{ fontSize: '0.8rem' }} />
                  </Link>
                ) : (
                  <Typography variant="body2" style={{ fontSize: '0.8rem' }}>
                    {rg.resource_group}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Tooltip
                  title={rg.account_id ?? ''}
                  arrow
                  placement="top-start"
                  enterDelay={500}
                >
                  <Typography variant="body2" style={{ fontSize: '0.8rem' }}>
                    {subscriptionLabel}
                  </Typography>
                </Tooltip>
              </TableCell>
              {tagKey && (
                <TableCell>
                  {fixedAt === undefined ? (
                    <Tooltip
                      arrow
                      title={`No ${tagKey} tag on this resource group when the scorecard last read Azure.`}
                    >
                      <span className={`${classes.status} ${classes.notSet}`}>
                        <ErrorOutlineIcon style={{ fontSize: '0.9rem' }} />
                        Not set
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      arrow
                      title={`The ${tagKey} tag was set in Azure at ${formatTime(
                        fixedAt,
                      )}. The scorecard re-reads Azure about every 3 hours, so it drops off this list on the next refresh.`}
                    >
                      <span className={`${classes.status} ${classes.waiting}`}>
                        <ScheduleIcon style={{ fontSize: '0.9rem' }} />
                        Waiting for refresh
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
